import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { money, shortDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Badge, Button, PageHeader, Input, Modal } from "@/components/kit";
import CustomerDocuments from "@/components/CustomerDocuments";
import NomineesEditor from "@/components/NomineesEditor";
import { CaretLeft, PencilSimpleLine, Users as UsersIcon, Warning, Check, X as XIcon } from "@phosphor-icons/react";

export default function CustomerDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [savings, setSavings] = useState([]);
  const [loans, setLoans] = useState([]);
  const [editRequests, setEditRequests] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const refreshCustomer = useCallback(async () => {
    try {
      const c = await api.get(`/customers/${id}`);
      setCustomer(c.data);
    } catch { /* ignore */ }
  }, [id]);

  const refreshRequests = useCallback(async () => {
    try {
      const r = await api.get(`/customers/${id}/edit-requests`);
      setEditRequests(r.data);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    (async () => {
      const [c, s, l] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/savings?customer_id=${id}`),
        api.get(`/loans?customer_id=${id}`),
      ]);
      setCustomer(c.data);
      setSavings(s.data);
      setLoans(l.data);
    })().catch(() => {});
    refreshRequests();
  }, [id, refreshRequests]);

  if (!customer) return <div className="p-8 text-slate-500">Loading…</div>;

  const pending = editRequests.filter((r) => r.status === "pending");
  const isSuperAdmin = user.role === "super_admin";
  const canEditCustomer = ["super_admin", "branch_admin", "agent", "staff"].includes(user.role);
  const nominees = customer.nominees || [];

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <Link to="/customers" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-4 hover:text-slate-800" data-testid="back-to-customers">
        <CaretLeft size={14} /> Back to customers
      </Link>
      <PageHeader
        title={<span className="inline-flex items-center gap-3 flex-wrap">
          {customer.name}
          {customer.customer_code && <span className="mono text-sm text-slate-500 font-normal">{customer.customer_code}</span>}
          {customer.kyc_complete ? (
            <Badge tone="success" data-testid="cust-kyc-complete">KYC complete</Badge>
          ) : (
            <Badge tone="warning" data-testid="cust-kyc-pending">KYC pending · {(customer.kyc_kinds || []).length}/3</Badge>
          )}
          {pending.length > 0 && (
            <Badge tone="warning" data-testid="cust-pending-edit-badge">{pending.length} pending edit</Badge>
          )}
        </span>}
        subtitle={<>
          <span className="mono">{customer.phone}</span>
          {customer.email && <> · {customer.email}</>}
        </>}
        action={
          canEditCustomer && (
            <Button onClick={() => setEditOpen(true)} data-testid="edit-customer-btn">
              <PencilSimpleLine size={14} weight="bold" />
              Edit details
            </Button>
          )
        }
      />

      {pending.length > 0 && (
        <PendingEditBanner
          customerId={customer.id}
          request={pending[0]}
          isSuperAdmin={isSuperAdmin}
          onChanged={async () => {
            await refreshRequests();
            await refreshCustomer();
          }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Address</div>
          <div className="text-sm text-slate-800">{customer.address || "—"}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Identity</div>
          <div className="text-sm text-slate-800">
            {customer.id_type ? <>{customer.id_type} · <span className="mono">{customer.id_number}</span></> : "—"}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Enrolled</div>
          <div className="text-sm text-slate-800">{shortDate(customer.created_at)}</div>
        </Card>
      </div>

      {/* NOMINEES */}
      <Card className="mb-6">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="heading font-semibold inline-flex items-center gap-2">
            <UsersIcon size={16} /> Nominees
          </h3>
          <span className="text-xs text-slate-500">{nominees.length} on file</span>
        </div>
        {nominees.length === 0 ? (
          <div className="text-sm text-slate-500 p-6 text-center" data-testid="no-nominees">
            No nominee on file. {canEditCustomer && (isSuperAdmin ? "Click Edit details to add one." : "Click Request edit to propose one.")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="nominees-list">
            {nominees.map((n, i) => (
              <div key={n.id || i} className="px-5 py-3 flex items-start justify-between gap-4" data-testid={`nominee-view-${i}`}>
                <div>
                  <div className="font-medium text-slate-900">
                    {n.name}
                    {n.relation && <span className="text-slate-500 text-sm font-normal ml-2">({n.relation})</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-4 gap-y-1">
                    {n.phone && <span className="mono">☎ {n.phone}</span>}
                    {n.dob && <span>DOB {shortDate(n.dob)}</span>}
                    {n.address && <span>{n.address}</span>}
                  </div>
                </div>
                {n.share_pct != null && (
                  <Badge tone="info">Share {n.share_pct}%</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <CustomerDocuments customerId={customer.id} onChange={refreshCustomer} />

      <Card className="mb-6">
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="heading font-semibold">Savings accounts</h3>
          <Link to={`/savings?customer_id=${customer.id}`} className="text-xs text-emerald-700 hover:underline" data-testid="view-all-savings">View all →</Link>
        </div>
        {savings.length === 0 ? (
          <div className="text-sm text-slate-500 p-6 text-center">No savings accounts.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {savings.map((s) => (
              <Link to={`/savings/${s.id}`} key={s.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50" data-testid={`cust-savings-${s.id}`}>
                <div>
                  <div className="font-medium text-slate-900 capitalize">{productLabel(s.product)}</div>
                  <div className="text-xs text-slate-500">
                    {s.account_no && <span className="mono">{s.account_no}</span>}
                    {s.account_no && <> · </>}
                    Rate {s.interest_rate}% · {s.interest_method}
                  </div>
                </div>
                <div className="text-right">
                  <div className="mono text-emerald-700 font-medium">{money(s.balance)}</div>
                  {s.maturity_value && <div className="text-xs text-slate-500 mono">Maturity {money(s.maturity_value)}</div>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="heading font-semibold">Loans</h3>
          <Link to={`/loans?customer_id=${customer.id}`} className="text-xs text-emerald-700 hover:underline" data-testid="view-all-loans">View all →</Link>
        </div>
        {loans.length === 0 ? (
          <div className="text-sm text-slate-500 p-6 text-center">No loans.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {loans.map((l) => (
              <Link to={`/loans/${l.id}`} key={l.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50" data-testid={`cust-loan-${l.id}`}>
                <div className="flex items-center gap-3">
                  <Badge tone="info">{l.loan_type}</Badge>
                  <div>
                    <div className="font-medium text-slate-900 mono">{money(l.principal)}</div>
                    <div className="text-xs text-slate-500">
                      {l.loan_no && <span className="mono">{l.loan_no}</span>}
                      {l.loan_no && <> · </>}
                      EMI {money(l.emi)}/day · {l.tenure_days} days
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="mono font-medium text-slate-900">{money(l.outstanding)}</div>
                  {l.overdue_count > 0 && <Badge tone="danger">{l.overdue_count} overdue</Badge>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <EditCustomerModal
        open={editOpen}
        customer={customer}
        isSuperAdmin={isSuperAdmin}
        onClose={() => setEditOpen(false)}
        onSaved={async () => {
          setEditOpen(false);
          await refreshCustomer();
          await refreshRequests();
        }}
      />
    </div>
  );
}

function PendingEditBanner({ customerId, request, isSuperAdmin, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/edit-requests/${request.id}/${kind}`, { note });
      setNote("");
      window.dispatchEvent(new Event("edit-requests-changed"));
      onChanged && onChanged();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this pending edit request?")) return;
    setBusy(true);
    try {
      await api.delete(`/edit-requests/${request.id}`);
      window.dispatchEvent(new Event("edit-requests-changed"));
      onChanged && onChanged();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const diff = request.diff || {};
  return (
    <Card className="mb-6 border-amber-200 bg-amber-50/40" data-testid="pending-edit-banner">
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <Warning size={18} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">
              Pending edit request
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              Raised by <span className="font-medium">{request.requested_by_name || "user"}</span>
              {request.requested_by_role && <span className="text-slate-400"> · {request.requested_by_role}</span>}
              {" · "}{shortDate(request.created_at)}
            </div>
            {request.reason && (
              <div className="text-sm text-slate-700 mt-2 italic">“{request.reason}”</div>
            )}

            <div className="mt-3 space-y-1.5" data-testid="pending-edit-diff">
              {Object.keys(diff).map((k) => (
                <div key={k} className="text-sm">
                  <span className="font-medium capitalize">{k.replace(/_/g, " ")}</span>
                  <div className="mt-0.5 pl-2 border-l-2 border-amber-200 text-xs">
                    <div className="text-rose-700">− {formatValue(diff[k].old)}</div>
                    <div className="text-emerald-700">+ {formatValue(diff[k].new)}</div>
                  </div>
                </div>
              ))}
            </div>

            {isSuperAdmin && (
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <Input
                  className="flex-1 min-w-[200px]"
                  placeholder="Review note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  testId="review-note-input"
                />
                <Button
                  onClick={() => act("approve")}
                  disabled={busy}
                  data-testid="approve-edit-btn"
                >
                  <Check size={14} weight="bold" /> Approve
                </Button>
                <Button
                  variant="danger"
                  onClick={() => act("reject")}
                  disabled={busy}
                  data-testid="reject-edit-btn"
                >
                  <XIcon size={14} weight="bold" /> Reject
                </Button>
              </div>
            )}
            {!isSuperAdmin && (
              <div className="mt-3">
                <button
                  onClick={cancel}
                  disabled={busy}
                  data-testid="cancel-edit-btn"
                  className="text-xs text-slate-600 underline hover:text-rose-700"
                >
                  Cancel this request
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function EditCustomerModal({ open, onClose, onSaved, customer, isSuperAdmin }) {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [nominees, setNominees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [policy, setPolicy] = useState({}); // {field: [roles]}
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    setForm({
      name: customer.name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      address: customer.address || "",
      id_type: customer.id_type || "",
      id_number: customer.id_number || "",
      notes: customer.notes || "",
    });
    setNominees(customer.nominees || []);
    setReason("");
    setErr("");
    // Load policy for non-super-admin so we can split direct vs approval fields
    if (!isSuperAdmin) {
      api.get("/settings/edit-policy").then((r) => setPolicy(r.data.policy || {})).catch(() => setPolicy({}));
    } else {
      setPolicy({});
    }
  }, [open, customer, isSuperAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    const changed = {};
    const strFields = ["name", "phone", "email", "address", "id_type", "id_number", "notes"];
    for (const k of strFields) {
      const cur = customer[k] || "";
      const nxt = (form[k] || "").trim();
      if (cur !== nxt) changed[k] = nxt || null;
    }
    const cleanNominees = (nominees || [])
      .filter((n) => n && n.name && n.name.trim())
      .map((n) => ({ ...n, share_pct: n.share_pct === "" ? null : n.share_pct }));
    if (JSON.stringify(customer.nominees || []) !== JSON.stringify(cleanNominees)) {
      changed.nominees = cleanNominees;
    }
    if (Object.keys(changed).length === 0) {
      setErr("Nothing to update.");
      setSaving(false);
      return;
    }

    try {
      if (isSuperAdmin) {
        await api.patch(`/customers/${customer.id}`, changed);
      } else {
        // Split by policy: direct vs approval
        const direct = {};
        const needApproval = {};
        for (const [k, v] of Object.entries(changed)) {
          if ((policy[k] || []).includes(user.role)) direct[k] = v;
          else needApproval[k] = v;
        }
        if (Object.keys(direct).length) {
          await api.patch(`/customers/${customer.id}`, direct);
        }
        if (Object.keys(needApproval).length) {
          await api.post(`/customers/${customer.id}/edit-requests`, { changes: needApproval, reason });
          window.dispatchEvent(new Event("edit-requests-changed"));
        }
      }
      onSaved && onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  // Show a small hint about split, if applicable
  const currentDirectFields = !isSuperAdmin
    ? Object.entries(policy).filter(([, roles]) => (roles || []).includes(user.role)).map(([f]) => f)
    : [];

  return (
    <Modal open={open} onClose={onClose} title={isSuperAdmin ? "Edit customer details" : "Edit customer"} maxWidth="max-w-xl">
      <form onSubmit={submit} className="space-y-4">
        {!isSuperAdmin && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {currentDirectFields.length === 0
              ? "All of your changes will be sent to Super Admin for approval before they are applied."
              : (
                <>
                  You can edit <span className="font-semibold">{currentDirectFields.map(fieldPretty).join(", ")}</span> directly.
                  Any other changes will be sent to Super Admin for approval.
                </>
              )}
          </div>
        )}
        <Input label="Full name" required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} testId="edit-name" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Phone" required value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} testId="edit-phone" />
          <Input label="Email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} testId="edit-email" />
        </div>
        <Input label="Address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} testId="edit-address" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="ID type" value={form.id_type || ""} onChange={(e) => setForm({ ...form, id_type: e.target.value })} testId="edit-idtype" />
          <Input label="ID number" value={form.id_number || ""} onChange={(e) => setForm({ ...form, id_number: e.target.value })} testId="edit-idnum" />
        </div>
        <Input label="Notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} testId="edit-notes" />

        <div className="pt-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Nominees</div>
          <NomineesEditor value={nominees} onChange={setNominees} compact />
        </div>

        {!isSuperAdmin && (
          <Input
            label="Reason for changes needing approval (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            testId="edit-reason"
            placeholder="Why are you requesting these changes?"
          />
        )}

        {err && <div className="text-sm text-rose-600" data-testid="edit-error">{String(err)}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="submit-edit-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function fieldPretty(f) {
  const M = { name: "Name", phone: "Phone", email: "Email", address: "Address", id_type: "ID type", id_number: "ID number", agent_id: "Agent", notes: "Notes", nominees: "Nominees" };
  return M[f] || f;
}

function formatValue(v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.map((n) => n.name || "(nominee)").join(", ") || "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

const productLabel = (p) => ({ daily: "Daily Savings", rd: "Recurring Deposit", fd: "Fixed Deposit" }[p] || p);
